import { BookmarkIcon } from 'ui-components/dist/BookmarkIcon';
import { ChatIcon } from 'ui-components/dist/ChatIcon';
import { ClickIcon } from 'ui-components/dist/ClickIcon';
import { EyeIcon } from 'ui-components/dist/EyeIcon';
import { HeartIcon } from 'ui-components/dist/HeartIcon';
import { PromoteIcon } from 'ui-components/dist/PromoteIcon';
import { ReachIcon } from 'ui-components/dist/ReachIcon';
import { SaveIcon } from 'ui-components/dist/SaveIcon';
import { ShareIcon } from 'ui-components/dist/ShareIcon';
import { ThumbUpIcon } from 'ui-components/dist/ThumbUpIcon';
import { SOCIAL_PLATFORMS } from '~/constants';
import { ASSET_TYPE } from '~/searchApi';
import { CONTENT_TYPE } from '~/types';
import { formatDecimal, formatInteger, formatPercentage } from '~/utils';

import { IContentDetailMetricsPlatformMetrics } from '.';

// Descriptions copys based in spreadsheets https://docs.google.com/spreadsheets/d/1Ur-avBDhCTfvlHrXMdd04NtBaojVmiaTKYVidlOuGx0/edit#gid=840031888

export const PLATFORM_METRICS: IContentDetailMetricsPlatformMetrics = {
  [CONTENT_TYPE.Post]: {
    viewsCount: {
      description: 'Total number of times the post has been seen.',
      icon: EyeIcon,
      title: 'Views',
      transformValue: formatInteger,
    },
    impressions: {
      description: 'Total number of times the post has been seen.',
      icon: EyeIcon,
      title: 'Impressions',
      transformValue: formatInteger,
      hideInPopover: true,
    },
    reach: {
      description: 'Total number of unique accounts that have seen the post.',
      icon: ReachIcon,
      title: 'Reach',
      transformValue: formatDecimal,
    },
    engagements: {
      description: 'A sum of likes, comments, saves and shares on the post.',
      icon: ClickIcon,
      metrics: {
        likes: {
          description: undefined,
          icon: ThumbUpIcon,
          title: 'Likes',
          transformValue: formatInteger,
        },
        comments: {
          description: undefined,
          icon: ChatIcon,
          title: 'Comments',
          transformValue: formatInteger,
        },
        saved: {
          description: undefined,
          icon: SaveIcon,
          title: 'Saves',
          transformValue: formatInteger,
        },
        shares: {
          description: 'The number of times the post has been shared.',
          icon: ShareIcon,
          title: 'Shares',
          transformValue: formatInteger,
        },
      },
      title: 'Engagements',
      transformValue: formatInteger,
    },
    postFollowersEngRate: {
      description: 'Percentage of followers who have engaged with the post.',
      icon: BookmarkIcon,
      title: 'Follower Engagement Rate',
      transformValue: formatPercentage,
    },
    postReachEngRate: {
      description:
        'Percentage of unique accounts who have seen and engaged with the post.',
      icon: PromoteIcon,
      title: 'Reach Engagement Rate',
      transformValue: formatPercentage,
    },
  },
  [CONTENT_TYPE.Story]: {
    viewsCount: {
      description: 'Number of times the story has been seen.',
      icon: EyeIcon,
      title: 'Views',
      transformValue: formatInteger,
    },
    impressions: {
      description: 'Number of times the story has been seen.',
      icon: EyeIcon,
      title: 'Impressions',
      transformValue: formatInteger,
      hideInPopover: true,
    },
    reach: {
      description: 'Number of unique accounts that have seen the story.',
      icon: ReachIcon,
      title: 'Reach',
      transformValue: formatDecimal,
    },
    shares: {
      description: undefined,
      icon: ShareIcon,
      title: 'Shares',
      transformValue: formatInteger,
    },
    replies: {
      description: undefined,
      icon: ChatIcon,
      title: 'Replies',
      transformValue: formatInteger,
    },
  },
  [CONTENT_TYPE.Reels]: {
    viewsCount: {
      description: 'Number of video sessions with 1 ms or more of playback.',
      icon: EyeIcon,
      isVisible: ({ type }) => type === ASSET_TYPE.video,
      title: 'Views',
      transformValue: formatDecimal,
    },
    igReelsAggregatedAllPlaysCount: {
      description: 'Number of video sessions with 1 ms or more of playback.',
      icon: EyeIcon,
      isVisible: ({ type }) => type === ASSET_TYPE.video,
      title: ({ type }) => (type === ASSET_TYPE.video ? 'Views' : 'Plays'),
      transformValue: formatDecimal,
      hideInPopover: true,
    },
    reach: {
      description:
        'Number of unique accounts that have seen the reel at least once.',
      icon: ReachIcon,
      title: 'Reach',
      transformValue: formatDecimal,
    },
    postEngagements: {
      description:
        'Total number of interactions, made up of likes, comments, saves, and shares.',
      icon: ClickIcon,
      metrics: {
        likes: {
          description: undefined,
          icon: ThumbUpIcon,
          title: 'Likes',
          transformValue: formatInteger,
        },
        comments: {
          description: undefined,
          icon: ChatIcon,
          title: 'Comments',
          transformValue: formatInteger,
        },
        saved: {
          description: undefined,
          icon: SaveIcon,
          title: 'Saves',
          transformValue: formatInteger,
        },
      },
      title: 'Engagements',
      transformValue: formatInteger,
      value: (content: { interactions: any }) => content?.interactions,
      isVisible: ({ interactions }) => !!interactions,
    },
    postFollowersEngRate: {
      description: 'Percentage of followers who have interacted with the reel.',
      icon: BookmarkIcon,
      title: 'Follower Engagement Rate',
      transformValue: formatPercentage,
    },
    postReachEngRate: {
      description:
        'Percentage of unique viewers who have interacted with the reel.',
      icon: PromoteIcon,
      title: 'Reach Engagement Rate',
      transformValue: formatPercentage,
    },
    viewsEngRate: {
      description: 'Percentage of engagements to total views on a reel.',
      icon: PromoteIcon,
      title: 'View Engagement Rate',
      isVisible: (content) => content?.type === ASSET_TYPE.video,
      transformValue: formatPercentage,
    },
  },
  [SOCIAL_PLATFORMS.tiktok]: {
    views: {
      description: undefined,
      icon: EyeIcon,
      title: 'Views',
      transformValue: formatDecimal,
    },
    engagements: {
      description: 'Sum of likes, comments and shares.',
      icon: ClickIcon,
      metrics: {
        likes: {
          description: undefined,
          icon: ThumbUpIcon,
          title: 'Likes',
          transformValue: formatInteger,
        },
        comments: {
          description: undefined,
          icon: ChatIcon,
          title: 'Comments',
          transformValue: formatInteger,
        },
        shares: {
          description: undefined,
          icon: ShareIcon,
          title: 'Shares',
          transformValue: formatInteger,
        },
      },
      title: 'Engagements',
      transformValue: formatInteger,
    },
    engagementRate: {
      description: 'Total engagements divided by video views.',
      icon: HeartIcon,
      title: 'View Engagement Rate',
      transformValue: formatPercentage,
    },
  },
  [SOCIAL_PLATFORMS.youtube]: {
    views: {
      description: undefined,

      title: 'Views',
      transformValue: formatInteger,
      metrics: {
        likes: {
          description: undefined,
          title: 'Likes',
          transformValue: formatInteger,
        },
        comments: {
          description: undefined,
          title: 'Comments',
          transformValue: formatInteger,
        },
      },
    },
    viewRate: {
      description:
        'Average percentage of a video watched during a video playback.',
      title: 'Average View Percentage',
      transformValue: formatPercentage,
    },
  },
};
